// src/lib/invoicePDF.js
// Generiert professionelle Rechnung im S&I. Editorial Stil
import { jsPDF } from 'jspdf';
import { getPackage, formatPrice } from './pricing';
import { buildEPCPayload, generateQRSVG, encodeToModules } from './qrGenerator';

// S&I. Farben
const COLORS = {
  black: [10, 10, 10],
  red: [196, 30, 58],
  gray: [102, 102, 102],
  lightGray: [229, 229, 229],
  white: [255, 255, 255],
};

// Firmendaten
const COMPANY = {
  name: 'S&I.',
  fullName: 'Iver Bohnes',
  street: 'Große Freiheit 82',
  city: '22767 Hamburg',
  country: 'Deutschland',
  email: 'wedding@sarahiver.de',
  website: 'siwedding.de',
  // Bankverbindung — Kontoinhaber muss exakt dem Namen bei der Bank
  // entsprechen (GiroCode-Empfänger), deshalb ohne Geburtsnamen
  accountHolder: 'Iver Bohnes',
  bank: 'N26',
  iban: 'DE76 1001 1001 2623 8509 68',
  bic: 'NTSBDEB1XXX',
  // Steuernummer: vom Finanzamt noch nicht vergeben. Pflichtangabe auf
  // Rechnungen (§ 14 Abs. 4 UStG) — sobald vorhanden hier eintragen.
  taxId: '',
};

/**
 * Generiert eine Rechnungsnummer
 */
function generateInvoiceNumber(project) {
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  const random = String(Date.now()).slice(-4);
  return `SI-${year}${month}-${random}`;
}

/**
 * Formatiert Datum auf Deutsch
 */
function formatDate(date) {
  return new Date(date).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Generiert Rechnung-PDF
 * @param {Object} project - Projektdaten
 * @param {Object} pricing - Preisberechnung
 * @param {Object} options - { invoiceNumber, invoiceDate, dueDate, isDeposit, depositAmount }
 */
export function generateInvoicePDF(project, pricing, options = {}) {
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 20; // Margin
  let y = 0;
  let pageNum = 1;

  const invoiceNumber = options.invoiceNumber || generateInvoiceNumber(project);
  const invoiceDate = options.invoiceDate || new Date();
  const dueDate = options.dueDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000); // +14 Tage
  const isDeposit = options.isDeposit || false;
  const isFinal = options.isFinal || false;

  // ============================================
  // HEADER
  // ============================================

  // Logo-Box oben links
  doc.setFillColor(...COLORS.black);
  doc.rect(m, 15, 35, 14, 'F');
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('S&I.', m + 17.5, 24, { align: 'center' });

  // Firmenadresse oben rechts
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  const headerRight = pw - m;
  doc.text(COMPANY.fullName, headerRight, 18, { align: 'right' });
  doc.text(COMPANY.street, headerRight, 23, { align: 'right' });
  doc.text(COMPANY.city, headerRight, 28, { align: 'right' });
  doc.text(COMPANY.email, headerRight, 33, { align: 'right' });

  y = 50;

  // ============================================
  // KUNDENADRESSE
  // ============================================

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text(`${COMPANY.fullName} · ${COMPANY.street} · ${COMPANY.city}`, m, y);
  y += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);
  doc.text(project.client_name || '[Kundenname]', m, y); y += 5;

  const street = [project.client_street, project.client_house_number].filter(Boolean).join(' ');
  if (street) { doc.text(street, m, y); y += 5; }

  const cityLine = [project.client_zip, project.client_city].filter(Boolean).join(' ');
  if (cityLine) { doc.text(cityLine, m, y); y += 5; }

  if (project.client_country && project.client_country !== 'Deutschland') {
    doc.text(project.client_country, m, y); y += 5;
  }

  // ============================================
  // RECHNUNGSDETAILS (rechte Spalte)
  // ============================================

  const detailsX = 130;
  let detailsY = 58;

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text('Rechnungsnummer:', detailsX, detailsY);
  doc.setTextColor(...COLORS.black);
  doc.setFont('helvetica', 'bold');
  doc.text(invoiceNumber, detailsX + 35, detailsY);
  detailsY += 6;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  doc.text('Rechnungsdatum:', detailsX, detailsY);
  doc.setTextColor(...COLORS.black);
  doc.text(formatDate(invoiceDate), detailsX + 35, detailsY);
  detailsY += 6;

  doc.setTextColor(...COLORS.gray);
  doc.text('Zahlungsziel:', detailsX, detailsY);
  doc.setTextColor(...COLORS.black);
  doc.text(formatDate(dueDate), detailsX + 35, detailsY);
  detailsY += 6;

  doc.setTextColor(...COLORS.gray);
  doc.text('Kundennummer:', detailsX, detailsY);
  doc.setTextColor(...COLORS.black);
  doc.text(`K-${project.id?.slice(0, 8) || '00000000'}`, detailsX + 35, detailsY);

  y = Math.max(y, detailsY) + 15;

  // ============================================
  // TITEL
  // ============================================

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);

  let title = 'RECHNUNG';
  if (isDeposit) title = 'ANZAHLUNGSRECHNUNG';
  if (isFinal) title = 'SCHLUSSRECHNUNG';

  doc.text(title, m, y);
  y += 12;

  // Projektinfo
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.gray);
  const couple = project.couple_names || `${project.partner1_name} & ${project.partner2_name}`;
  const wDate = project.wedding_date ? formatDate(project.wedding_date) : '';
  doc.text(`Projekt: Hochzeits-Website für ${couple}${wDate ? ` (${wDate})` : ''}`, m, y);
  y += 15;

  // ============================================
  // POSITIONEN TABELLE
  // ============================================

  // Tabellenkopf
  doc.setFillColor(...COLORS.black);
  doc.rect(m, y, pw - 2*m, 8, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.white);
  doc.text('Pos.', m + 3, y + 5.5);
  doc.text('Beschreibung', m + 15, y + 5.5);
  doc.text('Menge', pw - m - 55, y + 5.5);
  doc.text('Einzelpreis', pw - m - 35, y + 5.5);
  doc.text('Gesamt', pw - m - 3, y + 5.5, { align: 'right' });
  y += 12;

  const pkg = getPackage(project.package);
  let pos = 1;
  const positions = [];

  // Hauptpaket
  if (project.package === 'individual') {
    positions.push({
      pos: pos++,
      desc: 'Individual-Paket – Hochzeits-Website',
      qty: 1,
      unit: formatPrice(pricing.total),
      total: formatPrice(pricing.total),
    });
  } else {
    positions.push({
      pos: pos++,
      desc: `${pkg.name} – Hochzeitswebsite\n${pkg.hosting}. Alle Website-Komponenten, QR-Code`,
      qty: 1,
      unit: formatPrice(pricing.packagePrice),
      total: formatPrice(pricing.packagePrice),
    });

    // Addons
    if (pricing.addonsPrice > 0 && project.addons) {
      project.addons.forEach(addonId => {
        const addonNames = {
          save_the_date: 'Save the Date Seite',
          archive: 'Archiv-Seite',
          qr_code: 'QR-Code Erstellung',
          invitation_design: 'Einladungs-Design',
        };
        if (addonNames[addonId]) {
          const price = pricing.addonsPrice / project.addons.length; // Vereinfacht
          positions.push({
            pos: pos++,
            desc: addonNames[addonId],
            qty: 1,
            unit: formatPrice(price),
            total: formatPrice(price),
          });
        }
      });
    }

    // Custom Extras
    if (project.custom_extras && project.custom_extras.length > 0) {
      project.custom_extras.forEach(extra => {
        if (extra.amount > 0) {
          positions.push({
            pos: pos++,
            desc: extra.title || 'Zusatzleistung',
            qty: 1,
            unit: formatPrice(extra.amount),
            total: formatPrice(extra.amount),
          });
        }
      });
    }
  }

  // Positionen zeichnen
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...COLORS.black);

  positions.forEach((item, idx) => {
    const isLast = idx === positions.length - 1;
    const rowHeight = item.desc.includes('\n') ? 12 : 8;

    // Zebrastreifen
    if (idx % 2 === 1) {
      doc.setFillColor(250, 250, 250);
      doc.rect(m, y - 2, pw - 2*m, rowHeight, 'F');
    }

    doc.setFontSize(9);
    doc.text(String(item.pos), m + 3, y + 3);

    // Mehrzeilige Beschreibung
    const descLines = item.desc.split('\n');
    descLines.forEach((line, lineIdx) => {
      if (lineIdx === 0) {
        doc.setFont('helvetica', 'normal');
      } else {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.gray);
      }
      doc.text(line, m + 15, y + 3 + (lineIdx * 4));
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.black);
    doc.text(String(item.qty), pw - m - 55, y + 3);
    doc.text(item.unit, pw - m - 35, y + 3);
    doc.text(item.total, pw - m - 3, y + 3, { align: 'right' });

    y += rowHeight;

    // Trennlinie
    doc.setDrawColor(...COLORS.lightGray);
    doc.line(m, y, pw - m, y);
    y += 2;
  });

  y += 5;

  // ============================================
  // SUMMEN
  // ============================================

  const sumX = pw - m - 60;

  // Rabatt
  if (pricing.discount > 0) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.red);
    doc.text('Rabatt:', sumX, y);
    doc.text(`-${formatPrice(pricing.discount)}`, pw - m - 3, y, { align: 'right' });
    y += 8;
  }

  // Gesamt
  doc.setDrawColor(...COLORS.black);
  doc.setLineWidth(0.5);
  doc.line(sumX - 5, y - 2, pw - m, y - 2);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text('Gesamtbetrag:', sumX, y + 3);
  doc.setTextColor(...COLORS.red);

  let finalAmount = pricing.total;
  if (isDeposit) finalAmount = pricing.total * 0.5;
  if (isFinal && options.depositPaid) finalAmount = pricing.total * 0.5;

  doc.text(formatPrice(finalAmount), pw - m - 3, y + 3, { align: 'right' });

  y += 10;

  // Kleinunternehmerregelung
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(...COLORS.gray);
  doc.text('Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).', sumX - 5, y);
  y += 12;

  // ============================================
  // ZAHLUNGSPLAN
  // ============================================

  // Seitenumbruch-Helper
  const checkPageBreak = (needed) => {
    if (y + needed > ph - 25) {
      // Footer auf aktuelle Seite
      addFooter(doc, pageNum);
      doc.addPage();
      pageNum++;
      y = 25;
    }
  };

  if (!isDeposit && !isFinal) {
    checkPageBreak(35);
    doc.setFillColor(255, 250, 240);
    doc.rect(m, y, pw - 2*m, 28, 'F');
    doc.setDrawColor(...COLORS.lightGray);
    doc.rect(m, y, pw - 2*m, 28, 'S');
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.black);
    doc.text('Zahlungsplan', m + 5, y);
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.gray);
    doc.text(`1. Rate (50%): ${formatPrice(pricing.total / 2)} – fällig bei Vertragsabschluss`, m + 5, y);
    y += 5;
    doc.text(`2. Rate (50%): ${formatPrice(pricing.total / 2)} – fällig bei Go-Live der Website`, m + 5, y);
    y += 15;
  }

  if (isDeposit) {
    checkPageBreak(20);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.gray);
    doc.text('Dies ist eine Anzahlungsrechnung über 50% des Gesamtbetrags.', m, y);
    doc.text('Die Schlussrechnung erfolgt bei Fertigstellung der Website.', m, y + 5);
    y += 15;
  }

  if (isFinal && options.depositPaid) {
    checkPageBreak(15);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...COLORS.gray);
    doc.text(`Bereits gezahlte Anzahlung: ${formatPrice(pricing.total * 0.5)}`, m, y);
    y += 10;
  }

  // ============================================
  // BANKVERBINDUNG + QR-CODE (kombinierter Kasten)
  // ============================================

  checkPageBreak(55);
  y += 5;
  
  const bankBoxY = y;
  const bankBoxH = 48;
  const qrSize = 35;
  
  // Hintergrund-Kasten
  doc.setFillColor(248, 248, 248);
  doc.roundedRect(m, bankBoxY, pw - 2*m, bankBoxH, 2, 2, 'F');
  doc.setDrawColor(...COLORS.lightGray);
  doc.roundedRect(m, bankBoxY, pw - 2*m, bankBoxH, 2, 2, 'S');

  // Bankverbindung (links)
  y = bankBoxY + 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...COLORS.black);
  doc.text('Bankverbindung', m + 6, y);
  y += 6;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Kontoinhaber: ${COMPANY.accountHolder}`, m + 6, y); y += 4.5;
  doc.text(`IBAN: ${COMPANY.iban}`, m + 6, y); y += 4.5;
  doc.text(`BIC: ${COMPANY.bic}`, m + 6, y); y += 4.5;
  doc.text(`Bank: ${COMPANY.bank}`, m + 6, y); y += 4.5;
  doc.text(`Verwendungszweck: ${invoiceNumber}`, m + 6, y);

  // QR-Code (rechts im Kasten)
  try {
    const epcPayload = buildEPCPayload({
      iban: COMPANY.iban,
      bic: COMPANY.bic,
      name: COMPANY.accountHolder,
      amount: pricing.total * 0.5, // Immer 50% Rate
      reference: invoiceNumber,
    });
    
    const modules = encodeToModules(epcPayload);
    if (modules && modules.length > 0) {
      const qrX = pw - m - qrSize - 4;
      const qrY = bankBoxY + (bankBoxH - qrSize) / 2 - 1;
      const moduleCount = modules.length;
      const cellSize = (qrSize - 2) / (moduleCount + 8);
      const qrOffset = cellSize * 4;
      
      // QR white background
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(qrX, qrY, qrSize, qrSize, 1, 1, 'F');
      
      // Draw modules
      doc.setFillColor(26, 26, 26);
      for (let row = 0; row < moduleCount; row++) {
        for (let col = 0; col < moduleCount; col++) {
          if (modules[row][col]) {
            doc.rect(
              qrX + 1 + qrOffset + col * cellSize,
              qrY + 1 + qrOffset + row * cellSize,
              cellSize,
              cellSize,
              'F'
            );
          }
        }
      }
      
      // Label unter QR
      doc.setFontSize(5.5);
      doc.setTextColor(...COLORS.gray);
      doc.text('Mit deiner Banking-App', qrX + qrSize / 2, qrY + qrSize + 3.5, { align: 'center' });
      doc.text('scannen', qrX + qrSize / 2, qrY + qrSize + 6.5, { align: 'center' });
    }
  } catch (e) {
    console.warn('QR generation for invoice failed:', e);
  }

  y = bankBoxY + bankBoxH + 8;

  // Zahlungshinweis
  checkPageBreak(15);
  doc.setFontSize(8);
  doc.setTextColor(...COLORS.gray);
  doc.text(`Bitte überweisen Sie den Betrag bis zum ${formatDate(dueDate)} unter Angabe der Rechnungsnummer.`, m, y);
  y += 4;
  doc.text('Vielen Dank für Ihr Vertrauen!', m, y);

  // ============================================
  // FOOTER (auf jeder Seite)
  // ============================================
  
  addFooter(doc, pageNum);

  // ============================================
  // SPEICHERN ODER BASE64 ZURÜCKGEBEN
  // ============================================

  const typeLabel = isDeposit ? 'Anzahlung' : isFinal ? 'Schluss' : '';
  const filename = `SIwedding-Rechnung${typeLabel}-${project.slug || 'projekt'}-${invoiceNumber}.pdf`;

  if (options.returnBase64) {
    const base64 = doc.output('datauristring').split(',')[1];
    return { filename, invoiceNumber, invoiceDate: formatDate(invoiceDate), dueDate: formatDate(dueDate), base64 };
  }

  doc.save(filename);
  return { filename, invoiceNumber, invoiceDate: formatDate(invoiceDate), dueDate: formatDate(dueDate) };
}

// Footer-Helper (wird auf jeder Seite aufgerufen)
function addFooter(doc, pageNum) {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 20;

  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  const footerLine = `${COMPANY.fullName} · ${COMPANY.street} · ${COMPANY.city} · ${COMPANY.email}`;
  doc.text(footerLine, pw / 2, ph - 12, { align: 'center' });
  doc.text('Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.', pw / 2, ph - 8, { align: 'center' });
  doc.text(`Seite ${pageNum}`, pw - m, ph - 8, { align: 'right' });
}

export default { generateInvoicePDF };
